import React, { useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, X } from 'lucide-react';
import { useAudio } from '../context/AudioContext';

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

const GlobalAudioPlayer = () => {
  const {
    isPlaying,
    currentAudio,
    currentTime,
    duration,
    showMiniPlayer,
    togglePlayPause,
    seekTo,
    playNext,
    playPrevious,
    closeMiniPlayer,
    audioData,
    playbackRate,
    changePlaybackRate,
  } = useAudio();

  const [showSpeedMenu, setShowSpeedMenu] = useState(false);

  if (!showMiniPlayer || !currentAudio) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const currentIndex = audioData.findIndex(a => a.id === currentAudio.id);
  const canPlayPrevious = currentIndex > 0;
  const canPlayNext = currentIndex < audioData.length - 1;

  return (
    <div 
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] w-[95%] max-w-md"
      data-testid="global-audio-player"
    >
      <div 
        className="relative rounded-xl overflow-hidden shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(20, 20, 20, 0.98) 0%, rgba(30, 25, 20, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(198, 167, 94, 0.2)',
        }}
      >
        {/* Progress bar at top */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-black/30">
          <div 
            className="h-full transition-all duration-100"
            style={{ 
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #C6A75E, #D4AF37)',
            }}
          />
        </div>

        {/* Content */}
        <div className="flex items-center gap-3 p-3 pt-4">
          {/* Album art / Image */}
          <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 shadow-lg">
            <img 
              src={currentAudio.image} 
              alt={currentAudio.title}
              className="w-full h-full object-cover"
            />
            {/* Vinyl effect overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
          </div>

          {/* Track info */}
          <div className="flex-1 min-w-0">
            <p 
              className="text-sm font-medium truncate"
              style={{ 
                color: '#C6A75E',
                fontFamily: '"Cormorant Garamond", Georgia, serif',
              }}
            >
              {currentAudio.title}
            </p>
            <p className="text-xs text-gray-400 truncate">
              Ep. {currentAudio.episode} • {formatTime(currentTime)} / {formatTime(duration)}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1">
            {/* Playback Speed Button */}
            <div className="relative">
              <button
                onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                className="px-2 py-1 rounded text-xs font-medium transition-all text-white/70 hover:text-white hover:bg-white/10"
                style={{ minWidth: '40px' }}
                aria-label="Playback speed"
                data-testid="playback-speed-btn"
              >
                {playbackRate}x
              </button>
              
              {/* Speed menu */}
              {showSpeedMenu && (
                <div 
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-[#1a1a1a] border border-[#C6A75E]/30 rounded-lg shadow-xl overflow-hidden"
                  style={{ minWidth: '70px' }}
                >
                  {PLAYBACK_RATES.map((rate) => (
                    <button
                      key={rate}
                      onClick={() => {
                        changePlaybackRate(rate);
                        setShowSpeedMenu(false);
                      }}
                      className={`w-full px-3 py-2 text-xs text-center transition-colors ${
                        playbackRate === rate 
                          ? 'bg-[#C6A75E] text-black font-medium' 
                          : 'text-white/80 hover:bg-white/10'
                      }`}
                    >
                      {rate}x
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Previous */}
            <button
              onClick={playPrevious}
              disabled={!canPlayPrevious}
              className={`p-2 rounded-full transition-all ${
                canPlayPrevious 
                  ? 'text-white/80 hover:text-white hover:bg-white/10' 
                  : 'text-white/30 cursor-not-allowed'
              }`}
              aria-label="Previous track"
            >
              <SkipBack size={18} />
            </button>

            {/* Play/Pause */}
            <button
              onClick={togglePlayPause}
              className="p-2.5 rounded-full transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #C6A75E 0%, #A08040 100%)',
                boxShadow: '0 2px 8px rgba(198, 167, 94, 0.3)',
              }}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <Pause size={18} className="text-black" />
              ) : (
                <Play size={18} className="text-black ml-0.5" />
              )}
            </button>

            {/* Next */}
            <button
              onClick={playNext}
              disabled={!canPlayNext}
              className={`p-2 rounded-full transition-all ${
                canPlayNext 
                  ? 'text-white/80 hover:text-white hover:bg-white/10' 
                  : 'text-white/30 cursor-not-allowed'
              }`}
              aria-label="Next track"
            >
              <SkipForward size={18} />
            </button>

            {/* Close */}
            <button
              onClick={closeMiniPlayer}
              className="p-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-all ml-1"
              aria-label="Close player"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Clickable progress bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-1 cursor-pointer"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            seekTo(percent * duration);
          }}
        />
      </div>
    </div>
  );
};

export default GlobalAudioPlayer;
